import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  MessageCircle,
  Target,
  LayoutGrid,
  Layers,
  Loader2,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import { getProject as getLocalProject } from "../data/projects";
import { api, imgSrc } from "../lib/api";
import { waProjeto } from "../lib/site";

const Block = ({ icon: Icon, title, children, testid }) => (
  <Reveal>
    <section data-testid={testid} className="rounded-3xl border border-white/10 bg-white/[0.02] p-7 sm:p-9">
      <span className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#00E5FF]/20 to-[#8A2BE2]/20 ring-1 ring-white/15">
          <Icon className="h-5 w-5 text-[#00E5FF]" />
        </span>
        <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">{title}</h2>
      </span>
      <div className="mt-6">{children}</div>
    </section>
  </Reveal>
);

const ProjectPage = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(undefined);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    setProject(undefined);
    api
      .get(`/projects/${slug}`)
      .then(({ data }) => setProject(data))
      .catch((err) => {
        if (err?.response?.status === 404) {
          setProject(null);
        } else {
          setProject(getLocalProject(slug) || null);
        }
      });
  }, [slug]);

  if (project === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" data-testid="project-loading" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center">
        <h1 className="font-display text-3xl font-bold">Projeto não encontrado</h1>
        <Link
          to="/"
          data-testid="project-notfound-home-link"
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-7 py-3.5 text-sm font-semibold text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para o site
        </Link>
      </div>
    );
  }

  const hasUrl = project.url && project.url !== "#";

  return (
    <div data-testid="project-page" className="relative min-h-screen">
      <div className="pointer-events-none absolute -top-32 left-1/2 h-96 w-[50rem] -translate-x-1/2 rounded-full bg-[#5B8CFF]/10 blur-[150px]" />

      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#030305]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-6 lg:px-10">
          <Link to="/" aria-label="Voltar para a página inicial" data-testid="project-logo-link">
            <span className="relative inline-block h-9 w-9 rounded-lg bg-gradient-to-br from-[#00E5FF] to-[#8A2BE2] p-[1.5px]">
              <span className="flex h-full w-full items-center justify-center rounded-[7px] bg-[#050508]">
                <span className="text-gradient font-display text-lg font-black">E</span>
              </span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="project-back-link"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-medium text-white/80 transition-colors duration-300 hover:bg-white/10 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para o site
          </Link>
        </div>
      </div>

      <div className="relative mx-auto max-w-6xl px-6 pb-24 pt-16 lg:px-10 lg:pt-20">
        <Reveal>
          <span className="text-gradient text-[11px] font-semibold uppercase tracking-[0.3em]">
            {project.categoria}
          </span>
          <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
            {project.nome}
          </h1>
          <p className="mt-5 max-w-2xl text-base font-light leading-relaxed text-white/65 md:text-lg">
            {project.descricaoCompleta || project.descricao}
          </p>
        </Reveal>

        {project.imagem && (
          <Reveal delay={0.15}>
            <div className="mt-12 overflow-hidden rounded-3xl border border-white/10 bg-[#07070E]">
              <img
                src={imgSrc(project.imagem)}
                alt={`Preview do projeto ${project.nome} — ${project.categoria}`}
                className="w-full object-cover object-top"
                data-testid="project-hero-image"
              />
            </div>
          </Reveal>
        )}

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          {project.galeria?.length > 0 && (
            <Block icon={LayoutGrid} title="Galeria" testid="project-gallery">
              <div className={`grid gap-4 ${project.galeria.length > 1 ? "sm:grid-cols-2" : ""}`}>
                {project.galeria.map((img, i) => (
                  <img
                    key={i}
                    src={imgSrc(img)}
                    alt={`${project.nome} — imagem ${i + 1} da galeria`}
                    loading="lazy"
                    className="w-full rounded-2xl border border-white/10 object-cover object-top"
                    data-testid={`project-gallery-image-${i}`}
                  />
                ))}
              </div>
            </Block>
          )}

          <div className="flex flex-col gap-6">
            {project.funcionalidades?.length > 0 && (
              <Block icon={CheckCircle2} title="Principais funcionalidades" testid="project-features">
                <ul className="space-y-3.5">
                  {project.funcionalidades.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm font-light text-white/75 md:text-base">
                      <CheckCircle2 className="mt-0.5 shrink-0 text-[#00E5FF]" style={{ width: 18, height: 18 }} />
                      {f}
                    </li>
                  ))}
                </ul>
              </Block>
            )}

            {project.objetivo && (
              <Block icon={Target} title="Objetivo do projeto" testid="project-goal">
                <p className="text-sm font-light leading-relaxed text-white/75 md:text-base">
                  {project.objetivo}
                </p>
              </Block>
            )}

            {project.tecnologias?.length > 0 && (
              <Block icon={Layers} title="Tecnologias utilizadas" testid="project-tech">
                <div className="flex flex-wrap gap-2.5">
                  {project.tecnologias.map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs font-medium tracking-wide text-white/80"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </Block>
            )}
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-10 rounded-3xl bg-gradient-to-r from-[#00E5FF]/40 via-[#5B8CFF]/40 to-[#8A2BE2]/40 p-[1px]">
            <div className="flex flex-col items-start justify-between gap-7 rounded-[calc(1.5rem-1px)] bg-[#07070E] p-8 sm:flex-row sm:items-center sm:p-10">
              <div>
                <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                  {hasUrl ? "Veja o projeto no ar." : "Quer um projeto assim?"}
                </h2>
                <p className="mt-2 text-sm font-light text-white/60">
                  {hasUrl
                    ? "Acesse a versão publicada em uma nova aba."
                    : "O link público desta demonstração será adicionado em breve."}
                </p>
              </div>
              <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
                {hasUrl && (
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="project-visit-button"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-7 py-3.5 text-sm font-semibold text-white transition-[transform,filter] duration-300 hover:scale-[1.03] hover:brightness-110"
                  >
                    VISITAR PROJETO
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
                <a
                  href={waProjeto(project.nome)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-whatsapp-button"
                  className={`inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 ${
                    hasUrl
                      ? "border border-white/15 bg-white/5 hover:bg-white/10"
                      : "bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] hover:brightness-110"
                  }`}
                >
                  <MessageCircle className="h-4 w-4" />
                  Falar com a EDALIX
                </a>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 flex justify-center">
            <Link
              to="/"
              data-testid="project-back-bottom-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-white/55 transition-colors duration-300 hover:text-[#00E5FF]"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar para todos os projetos
            </Link>
          </div>
        </Reveal>
      </div>

      <div className="border-t border-white/10 py-7">
        <p className="text-center text-xs font-light text-white/40">
          © 2026 EDALIX Tecnologia. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
};

export default ProjectPage;
